import { Controller } from "@hotwired/stimulus"
import { ResourceLoadState } from "../lib/resource_load_state.mjs"

// Generic loading -> ready | failed boundary for a single image, iframe or component panel.
// The mask is enabled only after Stimulus connects. That keeps the server-rendered resource and
// its native fallback usable with JavaScript disabled and keeps domain controllers out of CSS.
export default class extends Controller {
  static targets = ["status"]
  static values = {
    autostart: Boolean,
    timeout: { type: Number, default: 15000 }
  }

  connect() {
    this.resource = this.element.querySelector("img, iframe, [data-loadable-content]")
    if (!this.resource) return

    this.state = new ResourceLoadState({
      onLoading: () => this.#renderLoading(),
      onReady: () => this.#renderReady(),
      onFailure: () => this.#renderFailed()
    })
    this.loaded = () => this.#loaded()
    this.resourceFailed = () => this.#failed()
    this.changed = () => this.#sourceChanged()
    this.resource.addEventListener("load", this.loaded)
    this.resource.addEventListener("error", this.resourceFailed)

    if (this.resource instanceof HTMLImageElement) {
      this.observer = new MutationObserver(this.changed)
      this.observer.observe(this.resource, { attributes: true, attributeFilter: ["src", "srcset"] })
      this.#connectImage()
    } else if (this.resource instanceof HTMLIFrameElement) {
      this.#begin(this.#source())
    } else if (this.autostartValue) {
      this.#begin(null)
    }
  }

  disconnect() {
    this.#cleanup()
  }

  cache() {
    this.#cleanup()
  }

  // Component controllers dispatch this before assigning a local object URL, which guarantees
  // that even an in-memory image begins behind the mask before its src changes.
  begin(event) {
    if (!this.resource) return
    this.#begin(event?.detail?.source || this.#source())
  }

  ready(event) {
    if (!this.state) return
    this.state.ready(this.ticket, event?.detail?.source || this.state.source)
  }

  failed(event) {
    if (!this.state) return
    this.state.fail(this.ticket, event?.detail?.source || this.state.source)
  }

  #connectImage() {
    if (!this.#source()) return this.#renderReady()
    this.#begin(this.#source())
    if (this.resource.complete) {
      if (this.resource.naturalWidth) this.#loaded()
      else this.#failed()
    }
  }

  #sourceChanged() {
    if (!this.#source()) return this.#renderReady()
    this.#begin(this.#source())
    if (this.resource.complete) {
      if (this.resource.naturalWidth) this.#loaded()
      else this.#failed()
    }
  }

  #begin(source) {
    this.ticket = this.state.start(source)
    clearTimeout(this.timer)
    const ticket = this.ticket
    this.timer = setTimeout(() => {
      if (!this.state.fail(ticket, source)) return

      this.element.dispatchEvent(new CustomEvent("loadable:timeout", { bubbles: true }))
    }, this.timeoutValue)
  }

  async #loaded() {
    const ticket = this.ticket
    const source = this.#source()
    if (this.resource instanceof HTMLImageElement && typeof this.resource.decode === "function") {
      try {
        await this.resource.decode()
      } catch {
        // A load followed by a decode rejection is a failed image, not a ready surface.
        return this.state.fail(ticket, source)
      }
    }
    this.state.ready(ticket, source)
  }

  #failed() {
    this.state.fail(this.ticket, this.#source())
  }

  #source() {
    return this.resource.src || this.resource.currentSrc || this.resource.getAttribute?.("src") || null
  }

  #renderLoading() {
    this.element.classList.add("loading-surface--loading")
    this.element.classList.remove("loading-surface--ready", "loading-surface--failed")
    this.element.setAttribute("aria-busy", "true")
    if (this.resource instanceof HTMLImageElement) this.resource.classList.remove("hidden")
    this.resource.inert = true
    this.statusTarget.textContent = this.statusTarget.dataset.loadingMessage
  }

  #renderReady() {
    clearTimeout(this.timer)
    this.element.classList.remove("loading-surface--loading", "loading-surface--failed")
    this.element.classList.add("loading-surface--ready")
    this.element.removeAttribute("aria-busy")
    if (this.resource) this.resource.inert = false
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
  }

  #renderFailed() {
    clearTimeout(this.timer)
    this.element.classList.remove("loading-surface--loading", "loading-surface--ready")
    this.element.classList.add("loading-surface--failed")
    this.element.removeAttribute("aria-busy")
    this.resource.inert = false
    // A broken image must expose its neutral surface or explicit semantic fallback underneath,
    // not the browser's broken-image glyph. Other resources (especially native audio) remain
    // usable as fallback.
    if (this.resource instanceof HTMLImageElement) this.resource.classList.add("hidden")
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
  }

  #cleanup() {
    clearTimeout(this.timer)
    this.observer?.disconnect()
    this.resource?.removeEventListener("load", this.loaded)
    this.resource?.removeEventListener("error", this.resourceFailed)
    this.state?.cancel()
    this.element.classList.remove("loading-surface--loading")
    this.element.removeAttribute("aria-busy")
    if (this.resource) this.resource.inert = false
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
  }
}
